import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import api from '../utils/api';
import useInsightsCache from '../hooks/useInsightsCache';
import insightsAPI from '../api/insights';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import UpgradePrompt from '../components/entitlements/UpgradePrompt';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { refreshNearMeIfNeeded } from '../utils/eventLocation';
import { eventSignalTags } from '../utils/insightPreferences';
import '../styles/InsightEventsPage.css';

function NetworkingSignals({ preferences, eventLocation, onUpdate }) {
  const tags = eventSignalTags(preferences, eventLocation);

  return (
    <div className="preferences-summary preferences-summary--footer">
      <h3 className="preferences-summary-title">Your signals</h3>
      {tags.length > 0 ? (
        <div className="preference-tags">
          {tags.map((tag) => (
            <span key={`${tag.kind}-${tag.label}`} className={`preference-tag ${tag.kind}`}>
              {tag.label}
            </span>
          ))}
        </div>
      ) : null}
      <button type="button" className="update-preferences-link" onClick={onUpdate}>
        Update preferences
      </button>
    </div>
  );
}

const InsightEventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();
  const { showError, showSuccess } = useToast();
  const cache = useInsightsCache();
  const { has, isLoading: entitlementsLoading, loadFailed } = useEntitlements();
  const networkingOpen = has('insights_events');
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [userPreferences, setUserPreferences] = useState(null);
  const [eventLocation, setEventLocation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(''); // '' = placeholder "Filter Category"; 'all' = user chose "All categories"
  const [isRefreshing, setIsRefreshing] = useState(false);
  /** Set when the server answers 402: events are a Pro feature. */
  const [locked, setLocked] = useState(false);

  const fetchEventsData = useCallback(async () => {
    if (entitlementsLoading) return;
    setIsLoading(true);
    try {
      // Entitlement is known and this account cannot see events. Do not read
      // cache and do not ask the API — a warm cache must not paint listings.
      if (!networkingOpen && !loadFailed) {
        cache.clearCachedData('events');
        setEvents([]);
        setLocked(true);
        try {
          const preferencesResponse = await api.get('/api/user/insight-preferences');
          setUserPreferences(preferencesResponse.data.preferences || null);
          setEventLocation(preferencesResponse.data.eventLocation || null);
        } catch {
          setUserPreferences(null);
        }
        return;
      }

      const preferencesResponse = await api.get('/api/user/insight-preferences');
      const prefs = preferencesResponse.data.preferences;
      setUserPreferences(prefs);
      setEventLocation(preferencesResponse.data.eventLocation || null);
      const nearMe = await refreshNearMeIfNeeded({
        preferences: prefs,
        eventLocation: preferencesResponse.data.eventLocation,
      });

      if (!prefs) {
        setEvents([]);
        setLocked(false);
        return;
      }

      const cachedEvents =
        networkingOpen && nearMe.status !== 'updated'
          ? cache.getCachedData('events', cache.CACHE_DURATION.events)
          : null;

      if (cachedEvents) {
        setLocked(false);
        setEvents(cachedEvents);
      } else {
        const response = await insightsAPI.getEventsSummary(prefs);
        const eventsData = response.events || response;
        setLocked(false);
        setEvents(eventsData);
        if (networkingOpen) cache.setCachedData('events', eventsData);
      }
    } catch (error) {
      if (error.response?.status === 402 || !networkingOpen) {
        cache.clearCachedData('events');
        setEvents([]);
        setLocked(true);
      } else {
        console.error('Error fetching events:', error);
        showError('Failed to load events data');
      }
    } finally {
      setIsLoading(false);
    }
    // cache callbacks are stable; the cache object itself is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError, entitlementsLoading, networkingOpen, loadFailed]);

  useEffect(() => {
    fetchEventsData();
  }, [fetchEventsData]);

  const handleRefreshEvents = async () => {
    // Check refresh limits
    if (!cache.canRefresh('events')) {
      showError(`You've reached your daily limit of ${cache.MAX_DAILY_REFRESHES.events} event refreshes. Try again tomorrow!`);
      return;
    }

    setIsRefreshing(true);
    try {
      const preferencesResponse = await api.get('/api/user/insight-preferences');
      const prefs = preferencesResponse.data.preferences;
      setUserPreferences(prefs);
      setEventLocation(preferencesResponse.data.eventLocation || null);
      await refreshNearMeIfNeeded({
        preferences: prefs,
        eventLocation: preferencesResponse.data.eventLocation,
        force: true,
      });

      if (prefs) {
        const eventsResponse = await api.post('/api/insights/events-summary?force=true', {
          preferences: prefs,
          forceRefresh: true,
        });
        const eventsData = eventsResponse.data.events || eventsResponse.data;
        setEvents(eventsData);
        cache.setCachedData('events', eventsData);
        
        // Increment refresh count
        cache.incrementRefreshCount('events');
        const remaining = cache.getRemainingRefreshes('events');
        showSuccess(`Events refreshed! ${remaining} refreshes remaining today.`);
      }
    } catch (error) {
      if (error.response?.status === 402) {
        cache.clearCachedData('events');
        setEvents([]);
        setLocked(true);
      } else {
        console.error('Error refreshing events:', error);
        showError('Failed to refresh events');
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear(),
      fullDate: date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  };

  const getFilterOptions = () => {
    if (!events || events.length === 0) return ['all'];
    
    const filterOptions = ['all'];
    
    // Get unique industries from actual events data
    const uniqueIndustries = [...new Set(events.map(event => event.industry).filter(Boolean))];
    
    // Map industry slugs to display names
    const industryDisplayMap = {
      'aerospace': 'Aerospace',
      'defence': 'Defence',
      'technology': 'Technology',
      'finance': 'Finance',
      'healthcare': 'Healthcare',
      'real-estate': 'Real Estate',
      'marketing': 'Marketing',
      'sales': 'Sales',
      'education': 'Education',
      'consulting': 'Consulting',
      'manufacturing': 'Manufacturing',
      'retail': 'Retail',
      'energy': 'Energy',
      'transportation': 'Transportation',
      'media-entertainment': 'Media & Entertainment',
      'non-profit': 'Non-profit',
      'government': 'Government',
      'legal': 'Legal',
      'construction': 'Construction',
      'agriculture': 'Agriculture',
      'hospitality': 'Hospitality',
      'fashion': 'Fashion',
      'food-beverage': 'Food & Beverage',
      'automotive': 'Automotive'
    };
    
    // Add industry options with display names
    uniqueIndustries.forEach(industry => {
      const displayName = industryDisplayMap[industry] || industry.charAt(0).toUpperCase() + industry.slice(1);
      filterOptions.push(displayName);
    });
    
    return filterOptions;
  };

  // Count events per category for dropdown labels (display only; filtering uses filteredEvents)
  const industrySlugMap = {
    'Aerospace': 'aerospace', 'Defence': 'defence', 'Technology': 'technology',
    'Finance': 'finance', 'Healthcare': 'healthcare', 'Real Estate': 'real-estate',
    'Marketing': 'marketing', 'Sales': 'sales', 'Education': 'education',
    'Consulting': 'consulting', 'Manufacturing': 'manufacturing', 'Retail': 'retail',
    'Energy': 'energy', 'Transportation': 'transportation', 'Media & Entertainment': 'media-entertainment',
    'Non-profit': 'non-profit', 'Government': 'government', 'Legal': 'legal',
    'Construction': 'construction', 'Agriculture': 'agriculture', 'Hospitality': 'hospitality',
    'Fashion': 'fashion', 'Food & Beverage': 'food-beverage', 'Automotive': 'automotive'
  };
  const getCategoryCount = (category) => {
    if (!events || events.length === 0) return 0;
    if (category === 'all') return events.length;
    const slug = industrySlugMap[category];
    return slug ? events.filter(e => e.industry === slug).length : 0;
  };

  const filteredEvents = (selectedCategory === '' || selectedCategory === 'all')
    ? events
    : events.filter(event => {
        // Filter by industry field
        if (event.industry) {
          // Map display name back to industry slug for comparison
          const industrySlugMap = {
            'Aerospace': 'aerospace',
            'Defence': 'defence',
            'Technology': 'technology',
            'Finance': 'finance',
            'Healthcare': 'healthcare',
            'Real Estate': 'real-estate',
            'Marketing': 'marketing',
            'Sales': 'sales',
            'Education': 'education',
            'Consulting': 'consulting',
            'Manufacturing': 'manufacturing',
            'Retail': 'retail',
            'Energy': 'energy',
            'Transportation': 'transportation',
            'Media & Entertainment': 'media-entertainment',
            'Non-profit': 'non-profit',
            'Government': 'government',
            'Legal': 'legal',
            'Construction': 'construction',
            'Agriculture': 'agriculture',
            'Hospitality': 'hospitality',
            'Fashion': 'fashion',
            'Food & Beverage': 'food-beverage',
            'Automotive': 'automotive'
          };
          
          const industrySlug = industrySlugMap[selectedCategory];
          return event.industry === industrySlug;
        }
        
        // Fallback: check if event has categories array
        if (event.categories && Array.isArray(event.categories)) {
          return event.categories.some(category => 
            category.toLowerCase() === selectedCategory.toLowerCase()
          );
        }
        
        // Fallback: check single category field
        return event.category === selectedCategory;
      });

  // Sort events by date - soonest first
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = new Date(a.date || a.startDate);
    const dateB = new Date(b.date || b.startDate);
    return dateA - dateB;
  });

  const getDaysUntilEvent = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const pageContent = entitlementsLoading || isLoading ? (
    <div className="insight-events-page loading">
      <div className="loading-spinner"></div>
      <p>Finding relevant events for you...</p>
    </div>
  ) : locked ? (
    <div className="insight-events-page">
      <UpgradePrompt
        feature="insights_events"
        title="Discover networking opportunities that matter to you"
        body="Find events matched to your industries and interests, so you can focus on the networking opportunities most relevant to you."
        ctaLabel="Explore Pro"
        ctaTo="/upgrade?focus=pro"
        showTierLine={false}
      />
      <NetworkingSignals
        preferences={userPreferences}
        eventLocation={eventLocation}
        onUpdate={() => navigate('/insight-preferences')}
      />
    </div>
  ) : (
    <div className="insight-events-page">
      <div className="events-header">
        <div className="events-overview">
          <p>Conferences, exhibitions and meetups matched to how you work.</p>
        </div>
        <div className="events-controls">
          <select
            className="events-filter-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="" disabled hidden>Filter Category</option>
            <option value="all">All categories ({events.length})</option>
            {getFilterOptions()
              .filter(category => category !== 'all')
              .map(category => {
                const count = getCategoryCount(category);
                return (
                  <option key={category} value={category}>
                    {category} ({count})
                  </option>
                );
              })}
          </select>
          <button
            type="button"
            className={`events-refresh-btn ${!cache.canRefresh('events') ? 'disabled' : ''}`}
            onClick={handleRefreshEvents}
            disabled={isRefreshing || !cache.canRefresh('events')}
            aria-label={isRefreshing ? 'Finding events' : 'Refresh events'}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            {isRefreshing
              ? 'Finding…'
              : !cache.canRefresh('events')
                ? 'Limit reached'
                : `Refresh (${cache.getRemainingRefreshes('events')} left)`}
          </button>
        </div>
      </div>

      <div className="events-content">
        {/* Filter Status */}
        {(selectedCategory !== '' && selectedCategory !== 'all') && (
          <div className="filter-status">
            <span className="filter-indicator">
              Showing {sortedEvents.length} of {events.length} events
              <span className="filter-category"> • {selectedCategory}</span>
            </span>
            <button
              className="clear-filter-button"
              onClick={() => setSelectedCategory('all')}
              type="button"
            >
              Clear Filter
            </button>
          </div>
        )}
        
        {sortedEvents.length === 0 ? (
          <div className="no-events">
            <div className="no-events-icon">
              <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <h3>No events found</h3>
            <p>We couldn't find any events matching your current preferences.</p>
            <button 
              className="update-preferences-button"
              onClick={() => navigate('/insight-preferences')}
            >
              Update Your Preferences
            </button>
          </div>
        ) : (
          <div className="events-grid">
            {sortedEvents.map((event, index) => {
              const dateInfo = formatDate(event.date);
              const daysUntil = getDaysUntilEvent(event.date);
              
              return (
                <div key={index} className="event-card">
                  <div className="event-date-badge">
                    <div className="date-day">{dateInfo.day}</div>
                    <div className="date-month">{dateInfo.month}</div>
                  </div>
                  
                  <div className="event-content">
                    <div className="event-header">
                      <span className="event-category">
                        {event.industry ? 
                          event.industry.charAt(0).toUpperCase() + event.industry.slice(1).replace('-', ' ') : 
                          event.category || 'Event'
                        }
                      </span>
                      <span className="event-type">{event.type || event.category || 'Event'}</span>
                    </div>
                    
                    <h3 className="event-title">{event.title}</h3>
                    <p className="event-description">{event.description}</p>
                    
                    <div className="event-details">
                      <div className="event-location">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        {event.location}
                      </div>
                      <div className="event-date">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        {dateInfo.fullDate}
                      </div>
                    </div>
                    
                    <div className="event-footer">
                      <div className="days-until">
                        {daysUntil > 0 ? (
                          <span className="upcoming">In {daysUntil} day{daysUntil > 1 ? 's' : ''}</span>
                        ) : daysUntil === 0 ? (
                          <span className="today">Today!</span>
                        ) : (
                          <span className="past">Past event</span>
                        )}
                      </div>
                      
                      <div className="event-actions">
                        <button 
                          className="learn-more-button"
                          onClick={() => window.open(event.url, '_blank')}
                          disabled={!event.url}
                        >
                          Learn More
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NetworkingSignals
        preferences={userPreferences}
        eventLocation={eventLocation}
        onUpdate={() => navigate('/insight-preferences')}
      />
    </div>
  );

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />
      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Networking Opportunities"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />
        <div className={homeStyles.hubContent}>
          {pageContent}
        </div>
      </div>
    </div>
  );
};

export default InsightEventsPage; 